import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, X, BookmarkPlus } from "lucide-react";
import { kindLabel, fmtDay } from "../../lib/litters";
import { Input } from "../ui/input";
import { api, errText } from "../../lib/api";
import { useLanguage } from "../../context/LanguageContext";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const BUILTIN = ["arrived", "visits", "pick", "home", "deposit"];

export function WaitlistMessageDialog({ litter, onClose }) {
  const { t, lang } = useLanguage();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [mine, setMine] = useState([]);
  const [tplName, setTplName] = useState(null);
  const loadMine = () => api.get("/my/message-templates").then((r) => setMine(r.data)).catch(() => {});
  const fill = (body) => body.replace(/\{breed\}/g, litter.breed).replace(/\{kind\}/g, kindLabel(litter.kind, t).toLowerCase())
    .replace(/\{ready\}/g, litter.ready_date ? `${t("tpl_on")} ${fmtDay(litter.ready_date, lang)}` : t("tpl_soon"));
  const saveTpl = async () => {
    try { await api.post("/my/message-templates", { name: tplName, body: text }); toast.success(t("tpl_saved")); setTplName(null); loadMine(); }
    catch (e) { toast.error(errText(e)); }
  };
  const delTpl = async (id) => { try { await api.delete(`/my/message-templates/${id}`); loadMine(); } catch (e) { toast.error(errText(e)); } };
  const load = () => litter && api.get(`/my/litters/${litter.id}/waitlist/messages`).then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { setText(""); setTplName(null); load(); if (litter) loadMine(); }, [litter]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/my/litters/${litter.id}/waitlist/message`, { message: text });
      toast.success(t("wm_sent_toast"));
      setText("");
      setTimeout(load, 1500);
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!litter} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto bg-white" data-testid="waitlist-message-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A]">{t("wm_title")}</DialogTitle>
          <DialogDescription className="text-slate-500">{t("wm_desc")}</DialogDescription>
        </DialogHeader>
        {litter && (
          <div data-testid="wm-templates">
            <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-1.5">{t("tpl_label")}</p>
            <div className="flex flex-wrap gap-1.5">
              {BUILTIN.map((k) => (
                <button key={k} type="button" onClick={() => setText(fill(t(`tpl_${k}_b`)))} data-testid={`wm-tpl-${k}`}
                  className="h-7 px-3 rounded-full border border-purple-200 bg-purple-50 text-purple-700 text-xs font-semibold hover:bg-purple-100">{t(`tpl_${k}_n`)}</button>
              ))}
              {mine.map((m) => (
                <span key={m.id} className="h-7 pl-3 pr-1 rounded-full border border-[#F5551E]/30 bg-[#FFF4F0] text-[#F5551E] text-xs font-semibold inline-flex items-center gap-1">
                  <button type="button" onClick={() => setText(fill(m.body))} data-testid={`wm-mytpl-${m.id}`}>{m.name}</button>
                  <button type="button" onClick={() => delTpl(m.id)} aria-label="Delete template" data-testid={`wm-mytpl-delete-${m.id}`} className="h-5 w-5 rounded-full hover:bg-[#F5551E]/10 flex items-center justify-center"><X size={11} /></button>
                </span>
              ))}
            </div>
          </div>
        )}
        <Textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} rows={5} placeholder={t("wm_ph")} data-testid="waitlist-message-text" className="rounded-xl" />
        {tplName === null ? (
          <button type="button" onClick={() => setTplName("")} disabled={!text.trim()} data-testid="wm-save-tpl" className="self-start text-xs font-semibold text-slate-500 hover:text-[#F5551E] inline-flex items-center gap-1 disabled:opacity-40"><BookmarkPlus size={13} /> {t("tpl_save")}</button>
        ) : (
          <div className="flex gap-2">
            <Input value={tplName} onChange={(e) => setTplName(e.target.value)} maxLength={40} placeholder={t("tpl_name_ph")} data-testid="wm-tpl-name" className="h-8 rounded-lg text-xs" />
            <Button size="sm" onClick={saveTpl} disabled={!tplName.trim()} data-testid="wm-tpl-save" className="h-8 rounded-lg">{t("bb_save")}</Button>
          </div>
        )}
        <Button onClick={send} disabled={busy || !text.trim()} data-testid="waitlist-message-send" className="rounded-xl bg-purple-600 hover:bg-purple-700 text-white gap-1.5">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {t("wm_send")} {litter?.waitlist_count} {t(litter?.waitlist_count === 1 ? "wm_person" : "wm_people")}
        </Button>
        <div>
          <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-2">{t("wm_history")}</p>
          {history.length === 0 ? <p className="text-xs text-slate-400">{t("wm_none")}</p> : (
            <div className="space-y-2" data-testid="waitlist-message-history">
              {history.map((m) => (
                <div key={m.id} className="rounded-xl bg-slate-50 px-3 py-2" data-testid={`waitlist-message-${m.id}`}>
                  <p className="text-sm text-[#12234A] whitespace-pre-line">{m.message}</p>
                  <p className="text-[10px] text-slate-400 mt-1">
                    {new Date(m.created_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "medium", timeStyle: "short" })} · {m.status === "sent" ? `${m.sent} ${t("wm_delivered")} · ${m.emailed} ${t("wm_emailed")}` : t("wm_sending")}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
